import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import { aiApi } from '../api/ai.api';
import { importApi, type ExtractedTripPlan } from '../api/import.api';
import { persistVenueProposal, persistDayPlanProposal, persistTipsProposal } from '../api/aiProposals';
import { importTickets, ticketCount, type TicketFiles } from '../api/ticketImport';
import {
  saveImportedDocuments,
  persistFlightProposal, persistHotelProposal, persistBookingProposal,
  persistTipProposal, persistExpenseProposal, persistPackingProposals,
} from '../api/importPersist';

export type TripAutoSuggestPhase = 'idle' | 'bookings' | 'places' | 'itinerary' | 'tips' | 'done' | 'error';
export type TripAutoSuggestError = 'insufficient_credits' | 'unknown';

export interface AiPrompts {
  placesPrompt: string;
  itineraryPrompt: string;
  tipsPrompt: string;
}

interface GenerateAllInput {
  tripId: string;
  /** AI suggestion prompts (from the wizard answers). Omit to skip AI generation. */
  aiPrompts?: AiPrompts;
  /** Plan extracted from uploaded documents. Omit to skip document import. */
  importPlan?: ExtractedTripPlan;
  /** Tickets sorted by kind in the wizard; whatever the AI can't read goes to the notes under `notesTitle`. */
  tickets?: { files: TicketFiles; notesTitle: string };
}

export interface GeneratedDay {
  dayNumber: number | null;
  stops: number;
}

interface TripAutoSuggestState {
  phase: TripAutoSuggestPhase;
  /** One line per ticket saved from the wizard's bookings step. */
  ticketNames: string[];
  /** Tickets the AI couldn't turn into a booking — kept in the notes instead. */
  unplacedTickets: number;
  /** Names of the venues persisted so far, in the order they landed. */
  venueNames: string[];
  days: GeneratedDay[];
  tipCount: number;
  error: TripAutoSuggestError | null;
}

type SetState = Dispatch<SetStateAction<TripAutoSuggestState>>;

const INITIAL_STATE: TripAutoSuggestState = { phase: 'idle', ticketNames: [], unplacedTickets: 0, venueNames: [], days: [], tipCount: 0, error: null };

function isInsufficientCreditsError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return status === 402 && code === 'INSUFFICIENT_AI_CREDITS';
}

// ── AI suggestion passes (places → itinerary → tips), auto-persisting each ──
async function runAiPasses(tripId: string, prompts: AiPrompts, setState: SetState): Promise<void> {
  const placesResult = await aiApi.chat(tripId, [{ role: 'user', content: prompts.placesPrompt }], 'places', { autoAccept: true });
  const venueActions = placesResult.actions.filter(a => a.type === 'venue_proposed');
  for (const action of venueActions) {
    if (action.type !== 'venue_proposed') continue;
    await persistVenueProposal(tripId, action.data);
    setState(s => ({ ...s, venueNames: [...s.venueNames, action.data.name] }));
  }

  if (venueActions.length > 0) {
    setState(s => ({ ...s, phase: 'itinerary' }));
    const itineraryResult = await aiApi.chat(tripId, [{ role: 'user', content: prompts.itineraryPrompt }], 'itinerary', { autoAccept: true });
    const dayPlanActions = itineraryResult.actions.filter(a => a.type === 'day_plan_proposed');
    for (const action of dayPlanActions) {
      if (action.type !== 'day_plan_proposed') continue;
      await persistDayPlanProposal(tripId, action.data);
      setState(s => ({ ...s, days: [...s.days, { dayNumber: action.data.dayNumber ?? null, stops: action.data.activities.length }] }));
    }
  }

  setState(s => ({ ...s, phase: 'tips' }));
  const tipsResult = await aiApi.chat(tripId, [{ role: 'user', content: prompts.tipsPrompt }], 'tips');
  const tipsActions = tipsResult.actions.filter(a => a.type === 'tips_proposed');
  for (const action of tipsActions) {
    if (action.type !== 'tips_proposed') continue;
    await persistTipsProposal(tripId, action.data);
  }
  const tipCount = tipsActions.reduce((sum, a) => sum + (a.type === 'tips_proposed' ? a.data.tips.length : 0), 0);
  setState(s => ({ ...s, tipCount: s.tipCount + tipCount }));
}

// Import is additive and best-effort: a single item with awkward data (a bad
// flight datetime, etc.) must never abort the rest of the import, so every
// persist is isolated. Failures are logged, not thrown.
async function safe(fn: () => Promise<unknown>): Promise<void> {
  try { await fn(); } catch (err) { console.warn('[import] skipped one item:', err); }
}

// ── Document import passes: materialize + persist every category. `advancePhase`
//    manages the places/itinerary overlay steps when import runs on its own. ──
async function runImportPasses(tripId: string, plan: ExtractedTripPlan, setState: SetState, advancePhase: boolean): Promise<void> {
  const res = await importApi.materialize(tripId, plan, { autoAccept: true });

  for (const v of res.venues) {
    await safe(async () => {
      await persistVenueProposal(tripId, v, 'IMPORT');
      setState(s => ({ ...s, venueNames: [...s.venueNames, v.name] }));
    });
  }

  if (res.dayPlans.length > 0) {
    if (advancePhase) setState(s => ({ ...s, phase: 'itinerary' }));
    for (const d of res.dayPlans) {
      await safe(async () => {
        await persistDayPlanProposal(tripId, d, 'IMPORT');
        setState(s => ({ ...s, days: [...s.days, { dayNumber: d.dayNumber ?? null, stops: d.activities.length }] }));
      });
    }
  }

  // Tickets, tips, packing and expenses persist without a dedicated overlay step.
  for (const f of res.flights)  await safe(() => persistFlightProposal(tripId, f));
  for (const h of res.hotels)   await safe(() => persistHotelProposal(tripId, h));
  for (const b of res.bookings) await safe(() => persistBookingProposal(tripId, b));
  for (const tp of res.tips)    await safe(() => persistTipProposal(tripId, tp));
  await safe(() => persistPackingProposals(tripId, res.packing));
  for (const e of res.expenses) await safe(() => persistExpenseProposal(tripId, e));
}

/**
 * Trip-onboarding generation. Runs the document import passes (if `importPlan`),
 * the AI suggestion passes (if `aiPrompts`), or both — complementary: the trip
 * gets everything the documents contain PLUS everything the AI proposes on top.
 * Everything auto-persists immediately (the wizard is the accept).
 */
export function useTripAutoSuggest() {
  const [state, setState] = useState<TripAutoSuggestState>(INITIAL_STATE);

  const generateAll = useCallback(async ({ tripId, aiPrompts, importPlan, tickets }: GenerateAllInput) => {
    const withTickets = !!tickets && ticketCount(tickets.files) > 0;
    setState({ ...INITIAL_STATE, phase: withTickets ? 'bookings' : 'places' });
    try {
      // Tickets before anything else: the itinerary pass plans around the
      // flight and the hotel only if they are already in the trip.
      if (withTickets) {
        const unplaced = await importTickets(tripId, tickets.files, name =>
          setState(s => ({ ...s, ticketNames: [...s.ticketNames, name] })));
        if (unplaced.length > 0) {
          setState(s => ({ ...s, unplacedTickets: unplaced.length }));
          await saveImportedDocuments(tripId, unplaced, tickets.notesTitle).catch(() => {});
        }
        setState(s => ({ ...s, phase: 'places' }));
      }
      // Documents first, AI second — not interchangeable: the AI passes only
      // know a place is already in the trip if it is in the database when they
      // run, so suggesting before importing guaranteed that the document's own
      // places came back as "new" suggestions. It also lets the itinerary pass
      // plan around the stops the document already fixed.
      if (importPlan) await runImportPasses(tripId, importPlan, setState, !aiPrompts);
      if (aiPrompts) await runAiPasses(tripId, aiPrompts, setState);
      setState(s => ({ ...s, phase: 'done' }));
    } catch (err) {
      setState(s => ({
        ...s,
        phase: 'error',
        error: isInsufficientCreditsError(err) ? 'insufficient_credits' : 'unknown',
      }));
    }
  }, []);

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  return {
    ...state,
    venueCount: state.venueNames.length,
    dayCount: state.days.length,
    generateAll,
    reset,
  };
}
